import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { AccountManager, SiteConfigManager } from '@atlas/core';
import { DefaultPopup, Label } from '@atlas/ui';

import { useQuickDepositPostHog } from 'hooks/usePostHog';
import { useNotificationsUIContext } from 'src/common/Popup/NotificationsUIContext';
import QuickDepositForm from 'src/common/Popup/QuickDepositPopup/QuickDepositForm';
import {
  checkDepositBonusTypeFits,
  getBonusMinDeposit,
  pickBonusWithLowestMinDeposit,
} from 'src/common/utils/depositBonusUtils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import useSetRedirectUrl from 'src/hooks/useSetRedirectUrl';

const QuickDepositPopup = ({ afterDelay, onClose, onCloseClick, setPaymentUrl }) => {
  const {
    handleCloseClick,
    handleDepositLoaderShown,
    handleDepositRedirectSuccess,
    handleDepositStart,
    handleDepositSuccess,
  } = useQuickDepositPostHog({
    afterDelay,
    onCloseClick,
  });
  const { isFlyingBetslip } = useNotificationsUIContext();
  const setRedirectUrl = useSetRedirectUrl();
  const currency = AccountManager.getAccountCurrency();
  const enableExtendedFlyingBetslip = SiteConfigManager.getConfig(
    'config.config.betslip.enableExtendedFlyingBetslip',
  );

  const { bonusesList, selectedBonus } = useBonusesContext();

  const listBonus = pickBonusWithLowestMinDeposit(bonusesList, currency);

  const bonus = selectedBonus || listBonus;

  const bonusTypeValue = bonus?.bonus?.bonusType ?? bonus?.bonusType ?? bonus?.type;
  const minBonusAmount = getBonusMinDeposit(bonus, currency);
  const showSubtitle = checkDepositBonusTypeFits(bonusTypeValue) && minBonusAmount > 0;

  const subTitle = showSubtitle ? (
    <Label
      message="quick_deposit_to_activate"
      params={{
        currency,
        minBonusAmount,
      }}
    />
  ) : null;

  return (
    <DefaultPopup
      className={classNames('quick-deposit', {
        'with-flying-betslip': isFlyingBetslip && !enableExtendedFlyingBetslip,
      })}
      headerTitle="quick_deposit_header"
      headerTitlePosition="left"
      onClose={handleCloseClick}
      postHeaderTitle={subTitle}
    >
      <QuickDepositForm
        closeQRCodeOnSuccess
        onClose={onClose}
        onDepositLoaderShown={handleDepositLoaderShown}
        onDepositRedirectSuccess={handleDepositRedirectSuccess}
        onDepositStart={handleDepositStart}
        onDepositSuccess={handleDepositSuccess}
        setPaymentUrl={setPaymentUrl}
        setRedirectUrl={setRedirectUrl}
      />
    </DefaultPopup>
  );
};

QuickDepositPopup.propTypes = {
  afterDelay: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  setPaymentUrl: PropTypes.func.isRequired,
};

export default QuickDepositPopup;
